import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWeb3React } from '@web3-react/core';
import { connectWallet, WalletConnectionError, ConnectionResult, isWalletInstalled, useSolanaWallet } from '../../web3/connectors';
import {
  getNetworkConfig,
  getNetworkDisplayName,
  isSupportedNetwork,
  getMainnetNetworks
} from '../../config/networks';
import { networkService } from '../../services/networkService';
import { errorLogger, ErrorCategory, ErrorSeverity } from '../../utils/errorLogger';
import { truncateAddress } from '../../utils/addressUtils';
import './WalletModal.css';

// Remove global declaration since it conflicts with web3-react types

interface WalletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ErrorState {
  message: string;
  type: 'error' | 'warning' | 'info';
  isVisible: boolean;
}

const WalletModal: React.FC<WalletModalProps> = ({ isOpen, onClose }) => {
  const { chainId, isActive, account } = useWeb3React();
  const { connect: connectSolana, isWalletInstalled: isSolanaWalletInstalled, connected: solanaConnected, publicKey: solanaPublicKey } = useSolanaWallet();
  const [currentView, setCurrentView] = useState<'wallets' | 'networks'>('wallets');
  const [isLoading, setIsLoading] = useState(false);
  const [, setErrorState] = useState<ErrorState>({ message: '', type: 'error', isVisible: false });
  const [, setConnectedAccount] = useState<string | null>(null);
  const [currentNetwork, setCurrentNetwork] = useState<{
    chainId: number;
    name: string;
    isSupported: boolean;
  } | null>(null);
  const [isNetworkSwitching, setIsNetworkSwitching] = useState(false);

  // Helper function to show error messages
  const showError = (message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    setErrorState({ message, type, isVisible: true });
    setTimeout(() => {
      setErrorState(prev => ({ ...prev, isVisible: false }));
    }, 5000); // Hide after 5 seconds
  };

  // Helper function to show success messages
  const showSuccess = (account: string) => {
    setConnectedAccount(account);
    showError(`Successfully connected: ${account.slice(0, 6)}...${account.slice(-4)}`, 'info');
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  // Helper function to get wallet installation status
  const getWalletInstallationStatus = (walletName: string): string | null => {
    // Check EVM wallets
    if (['MetaMask', 'Coinbase Wallet', 'OKX Wallet', 'Bitget Wallet'].includes(walletName)) {
      return isWalletInstalled(walletName) ? 'detected' : null;
    }
    // Check Solana wallets
    if (['Phantom', 'Solflare', 'MetaMask Snap'].includes(walletName)) {
      return isSolanaWalletInstalled(walletName as any) ? 'detected' : null;
    }
    return null;
  };

  const wallets = [
    {
      name: 'MetaMask',
      icon: '/metamask.png',
      description: 'Connect using browser wallet',
      status: getWalletInstallationStatus('MetaMask'),
      color: '#f6851b',
      type: 'evm' as const
    },
    {
      name: 'WalletConnect',
      icon: '/walletconnect.png',
      description: 'Scan with WalletConnect',
      status: null,
      color: '#3b99fc',
      type: 'evm' as const
    },
    {
      name: 'Coinbase Wallet',
      icon: '/coinbase.png',
      description: 'Connect using Coinbase Wallet',
      status: getWalletInstallationStatus('Coinbase Wallet'),
      color: '#0052ff',
      type: 'evm' as const
    },
    {
      name: 'Phantom',
      icon: '/phantom.png',
      description: 'Connect using Phantom wallet',
      status: getWalletInstallationStatus('Phantom'),
      color: '#ab9ff2',
      type: 'solana' as const
    },
    {
      name: 'Solflare',
      icon: '/solflare.png',
      description: 'Connect using Solflare wallet',
      status: getWalletInstallationStatus('Solflare'),
      color: '#fc4e6c',
      type: 'solana' as const
    },
    {
      name: 'MetaMask Snap',
      icon: '/metamask.png',
      description: 'Connect Solana via MetaMask Snap',
      status: getWalletInstallationStatus('MetaMask Snap'),
      color: '#f6851b',
      type: 'solana' as const
    },
    {
      name: 'OKX Wallet',
      icon: '/okx.png',
      description: 'Connect using OKX Wallet',
      status: getWalletInstallationStatus('OKX Wallet'),
      color: '#000000',
      type: 'evm' as const
    },
    {
      name: 'Bitget Wallet',
      icon: '/bitget.png',
      description: 'Connect using Bitget Wallet',
      status: getWalletInstallationStatus('Bitget Wallet'),
      color: '#00d4ff',
      type: 'evm' as const
    },
    {
      name: 'Safe',
      icon: '/safe.png',
      description: 'Connect using Safe Wallet',
      status: null,
      color: '#12ff80',
      type: 'evm' as const
    }
  ];

  // Initialize network service and detect current network
  useEffect(() => {
    const initializeNetwork = async () => {
      if (!isActive || !chainId) {
        setCurrentNetwork(null);
        return;
      }

      try {
        await networkService.initialize();
        
        getNetworkConfig(chainId);
        const isSupported = isSupportedNetwork(chainId);
        
        setCurrentNetwork({
          chainId,
          name: getNetworkDisplayName(chainId),
          isSupported
        });

        if (!isSupported) {
          showError(
            `You're connected to an unsupported network (${getNetworkDisplayName(chainId)}). Please switch to Ethereum or BSC.`,
            'warning'
          );
        }
      } catch (error) {
        console.error('Failed to initialize network:', error);
        errorLogger.logError(
          ErrorCategory.NETWORK_SWITCH,
          ErrorSeverity.MEDIUM,
          'Failed to initialize network in WalletModal',
          'Network initialization failed',
          { chainId, error },
          error as Error,
          true
        );
      }
    };

    initializeNetwork();
  }, [isActive, chainId]);

  // Get supported networks for display
  const supportedNetworks = getMainnetNetworks().map(network => ({
    chainId: network.chainId,
    name: network.displayName,
    icon: network.chainId === 1 ? '⟠' : '🔗', // Ethereum or BSC icon
    description: network.chainId === 1
      ? 'Ethereum Mainnet - Original blockchain'
      : 'BNB Smart Chain - Low fees, fast transactions',
    isActive: currentNetwork?.chainId === network.chainId
  }));

  const handleWalletConnect = async (walletName: string) => {
    setIsLoading(true);
    setErrorState({ message: '', type: 'error', isVisible: false });

    try {
      console.log('🔄 [DIAGNOSTIC] Wallet connection attempt:', {
        walletName,
        timestamp: new Date().toISOString(),
        currentActive: isActive,
        hasChainId: !!chainId,
        solanaConnected
      });

      // Handle Solana wallets
      const solanaWallets = ['Phantom', 'Solflare', 'MetaMask Snap'];
      if (solanaWallets.includes(walletName)) {
        // Check if Solana wallet is installed
        if (!isSolanaWalletInstalled(walletName as any)) {
          const installUrls: { [key: string]: string } = {
            'Phantom': 'https://phantom.app/download',
            'Solflare': 'https://solflare.com/download',
            'MetaMask Snap': 'https://metamask.io/download/'
          };

          const installUrl = installUrls[walletName];
          showError(`${walletName} is not installed. Please install it first.`, 'warning');
          if (installUrl) {
            setTimeout(() => {
              window.open(installUrl, '_blank');
            }, 2000);
          }
          setIsLoading(false);
          return;
        }

        // Connect Solana wallet
        const publicKey = await connectSolana(walletName as any);
        
        if (publicKey) {
          console.log(`✅ Successfully connected to ${walletName}:`, publicKey);
          showSuccess(publicKey);
          setIsLoading(false);
          return;
        }
      }

      // Check if EVM wallet is installed first
      if (!isWalletInstalled(walletName)) {
        const installUrls: { [key: string]: string } = {
          'MetaMask': 'https://metamask.io/download/',
          'Coinbase Wallet': 'https://www.coinbase.com/wallet',
          'OKX Wallet': 'https://www.okx.com/web3',
          'Bitget Wallet': 'https://web3.bitget.com/en/wallet'
        };

        const installUrl = installUrls[walletName];
        if (installUrl) {
          showError(`${walletName} is not installed. Please install it first.`, 'warning');
          setTimeout(() => {
            window.open(installUrl, '_blank');
          }, 2000);
        } else {
          showError(`${walletName} uses WalletConnect. Please scan the QR code when prompted.`, 'info');
        }
        setIsLoading(false);
        return;
      }

      // Attempt to connect using enhanced connector (EVM wallets)
      const result: ConnectionResult = await connectWallet(walletName);
      
      console.log('🔄 [DIAGNOSTIC] Wallet connection result:', {
        success: result.success,
        hasAccount: !!result.account,
        errorType: result.error?.type,
        timestamp: new Date().toISOString()
      });
      
      if (result.success && result.account) {
        console.log(`✅ Successfully connected to ${walletName}:`, result.account);
        showSuccess(result.account);
        
        // Force a small delay to allow Web3React state to update
        setTimeout(() => {
          console.log('🔄 [DIAGNOSTIC] Post-connection state check:', {
            isActive,
            chainId,
            account: result.account
          });
        }, 1000);
      } else if (result.error) {
        console.error('❌ [DIAGNOSTIC] Connection error:', result.error);
        handleConnectionError(result.error, walletName);
      }
      
    } catch (error: any) {
      console.error('🚨 [DIAGNOSTIC] Unexpected connection error:', error);
      showError(error.message || 'An unexpected error occurred. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectionError = (error: ConnectionResult['error'], walletName: string) => {
    if (!error) return;

    switch (error.type) {
      case WalletConnectionError.USER_REJECTED:
        showError('Connection was cancelled. Please try again and approve the connection request.', 'warning');
        break;
      case WalletConnectionError.WALLET_NOT_INSTALLED:
        showError(`${walletName} is not installed. Please install the wallet extension first.`, 'warning');
        break;
      case WalletConnectionError.NETWORK_ERROR:
        showError('Network connection failed. Please check your internet connection and try again.', 'error');
        break;
      case WalletConnectionError.CONNECTION_TIMEOUT:
        showError('Connection timed out. Please check your wallet and try again.', 'warning');
        break;
      case WalletConnectionError.INVALID_NETWORK:
        showError('Please switch to a supported network in your wallet.', 'warning');
        break;
      default:
        showError(error.message || 'Failed to connect. Please try again.', 'error');
    }
  };

  const handleNetworkSelect = async (networkChainId: number) => {
    if (currentNetwork?.chainId === networkChainId) {
      setCurrentView('wallets');
      return;
    }

    setIsNetworkSwitching(true);
    setErrorState({ message: '', type: 'error', isVisible: false });

    try {
      const switchResult = await networkService.switchToNetwork(networkChainId);
      
      if (switchResult.success) {
        showError(
          `Successfully switched to ${getNetworkDisplayName(networkChainId)}`,
          'info'
        );
        setCurrentView('wallets');
      } else {
        const errorMessage = switchResult.error?.message || 'Failed to switch network';
        
        if (switchResult.error?.code === 4902) {
          showError(
            `${getNetworkDisplayName(networkChainId)} network will be added to your wallet. Please approve the request.`,
            'info'
          );
        } else {
          showError(errorMessage, 'error');
        }
        
        errorLogger.logError(
          ErrorCategory.NETWORK_SWITCH,
          ErrorSeverity.MEDIUM,
          `Network switch failed: ${errorMessage}`,
          'Failed to switch network',
          { targetChainId: networkChainId, error: switchResult.error },
          new Error(errorMessage),
          true
        );
      }
    } catch (error) {
      console.error('Unexpected error during network switch:', error);
      showError('An unexpected error occurred while switching networks.', 'error');
      
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.HIGH,
        'Unexpected network switch error',
        'Network switch failed unexpectedly',
        { targetChainId: networkChainId, error },
        error as Error,
        true
      );
    } finally {
      setIsNetworkSwitching(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`wallet-modal-overlay ${isOpen ? 'open' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleBackdropClick}
        >
          <motion.div
            className={`wallet-modal ${isOpen ? 'open' : ''}`}
            initial={{ transform: 'translateX(100%)' }}
            animate={{ transform: 'translateX(0)' }}
            exit={{ transform: 'translateX(100%)' }}
            transition={{ type: 'tween', ease: 'easeOut', duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
              </div>
            )}

            <button className="wallet-modal-close" onClick={onClose}>
              ✕
            </button>

            {currentView === 'wallets' ? (
              <>
                <div className="wallet-modal-header">
                  <h2>Connect wallet</h2>
                  <p>Connect wallet to make transactions on the dApp</p>
                  
                  {/* Connection Status Indicator */}
                  {(isActive || solanaConnected) && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px 16px',
                      background: 'rgba(171, 159, 242, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(171, 159, 242, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>✅</span>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                            Connected to {solanaConnected ? 'Solana' : 'EVM'}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
                            {(account || solanaPublicKey) ? truncateAddress(account || solanaPublicKey || '') : ''}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (solanaConnected) {
                            // Disconnect Solana
                            const provider = (window as any).phantom?.solana || (window as any).solflare;
                            if (provider?.disconnect) {
                              await provider.disconnect();
                            }
                            localStorage.removeItem('solanaWalletName');
                            localStorage.removeItem('solanaPublicKey');
                          } else if (isActive) {
                            // Disconnect EVM
                            localStorage.removeItem('walletName');
                          }
                          window.location.reload();
                        }}
                        style={{
                          padding: '6px 12px',
                          background: 'rgba(255, 107, 107, 0.2)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#ff6b6b',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Disconnect
                      </button>
                    </div>
                  )}
                </div>

                <div className="wallet-modal-content">
                  <div className="wallet-section">
                    {wallets.map((wallet, index) => {
                      const needsInstallation = !wallet.status && wallet.name !== 'WalletConnect' && wallet.name !== 'Safe';
                      
                      return (
                        <motion.div
                          key={wallet.name}
                          className={`wallet-option glow-on-hover ${needsInstallation ? 'not-installed' : ''}`}
                          onClick={() => handleWalletConnect(wallet.name)}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="wallet-option-content">
                            <div className="wallet-icon">
                              <img
                                src={wallet.icon}
                                alt={`${wallet.name} icon`}
                                className="wallet-icon-image"
                              />
                            </div>
                            <div className="wallet-details">
                              <div className="wallet-name">{wallet.name}</div>
                              <div className="wallet-description">
                                {needsInstallation
                                  ? 'Not installed - Click to install'
                                  : wallet.description}
                              </div>
                            </div>
                          </div>
                          {wallet.status && (
                            <div className="wallet-status detected">{wallet.status}</div>
                          )}
                          {needsInstallation && (
                            <div className="wallet-status not-detected">Install</div>
                          )}
                          <div className="wallet-arrow">›</div>
                        </motion.div>
                      );
                    })}
                  </div>

                  <div className="wallet-section">
                    <h3>More wallets</h3>
                    <motion.div
                      className="wallet-option glow-on-hover"
                      onClick={() => setCurrentView('networks')}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <div className="wallet-option-content">
                        <div className="wallet-icon">🌐</div>
                        <div className="wallet-details">
                          <div className="wallet-name">More wallets</div>
                          <div className="wallet-description">Connect with additional wallet options</div>
                        </div>
                      </div>
                      <div className="wallet-arrow">›</div>
                    </motion.div>
                  </div>
                </div>

                <div className="wallet-modal-footer">
                  <p className="terms-text">
                    By connecting your wallet, you agree to our{' '}
                    <a href="/docs/terms-of-use" className="terms-links">Terms of Use</a> and{' '}
                    <button className="terms-links" style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit'}}>Privacy Policy</button>
                  </p>
                  <p className="update-date">Last update of Terms of Use: 15/05/2025</p>
                </div>
              </>
            ) : (
              <>
                <div className="wallet-modal-header">
                  <motion.button
                    className="back-button"
                    onClick={() => setCurrentView('wallets')}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      position: 'absolute',
                      left: '24px',
                      top: '32px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255, 255, 255, 0.8)',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ←
                  </motion.button>
                  <h2>Select networks to connect</h2>
                  <p>Select which networks you want to use with this account. You can have separate addresses for each network.</p>
                </div>

                <div className="wallet-modal-content">
                  <div className="network-section">
                    <div className="network-icon-container">
                      <div className="network-visualization">
                        <div className="network-center">🌐</div>
                        <div className="network-node"></div>
                        <div className="network-node"></div>
                        <div className="network-node"></div>
                        <div className="network-node"></div>
                        <div className="network-node"></div>
                        <div className="network-node"></div>
                      </div>
                    </div>

                    {supportedNetworks.map((network, index) => (
                      <motion.div
                        key={network.name}
                        className={`network-option glow-on-hover ${network.isActive ? 'active' : ''}`}
                        onClick={() => handleNetworkSelect(network.chainId)}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="network-icon">{network.icon}</div>
                        <div className="network-details">
                          <div className="network-name">{network.name}</div>
                          <div className="network-description">{network.description}</div>
                        </div>
                        {network.isActive && (
                          <div className="network-status active">Connected</div>
                        )}
                        {isNetworkSwitching && (
                          <div className="loading-spinner-small"></div>
                        )}
                        <div className="wallet-arrow">›</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WalletModal;